import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import WebUiPageWrapper from '../../components/page-wrapper';

type Slots = {
  booted?: 'system0' | 'system1';
  system0?: string;
  system1?: string;
};

export default function WebUiMaintenanceFwupdatePage() {
  const [slots, setSlots] = React.useState<Slots>({});
  const [file, setFile] = React.useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState('inactive');
  const [error, setError] = React.useState<string | null>(null);

  const loadSlots = React.useCallback(async () => {
    const data = await postWebUiApi<Slots>({
      service: 'system',
      access: 'get',
      command: 'slots',
    });
    setSlots(data);
  }, []);

  React.useEffect(() => {
    let alive = true;
    loadSlots()
      .then(() => alive && setError(null))
      .catch((err) => alive && setError((err as Error).message));
    return () => {
      alive = false;
    };
  }, [loadSlots]);

  const runAction = React.useCallback(
    async (command: string, ok: string) => {
      try {
        setLoading(true);
        await postWebUiApi({ service: 'system', access: 'set', command });
        setStatus(ok);
        setError(null);
        await loadSlots();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [loadSlots],
  );

  const bootedPartition =
    slots.booted === 'system1' ? 'Partition 2' : 'Partition 1';
  const bootedVersion =
    slots.booted === 'system1' ? slots.system1 : slots.system0;
  const altVersion = slots.booted === 'system1' ? slots.system0 : slots.system1;

  return (
    <WebUiPageWrapper titleList={['Maintenance', 'Software']}>
      {error ? <WebUiAlert type="error" message={error} /> : null}

      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">Software Update</h4>
        <table className="webui-table max-w-fit">
          <thead>
            <tr className="webui-table-row-head">
              <th>Setting</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="webui-table-row-content">
              <td>Booted Partition</td>
              <td>{bootedPartition}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Software Version of Booted Partition</td>
              <td>{bootedVersion ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Software Version of Alternate Partition</td>
              <td>
                <div className="float-left">{altVersion ?? '-'}</div>
                <button
                  type="button"
                  className="webui-button float-right px-2 py-0"
                  disabled={loading}
                  onClick={() =>
                    runAction(
                      'boot alternative partition',
                      'alternate partition selected',
                    )
                  }
                >
                  Boot Alternative Partition
                </button>
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Update File</td>
              <td>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Update Status</td>
              <td>{status}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Progress</td>
              <td>
                <progress max={100} value={uploadProgress} className="w-full" />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="flex w-full gap-2">
          <WebUiButton
            disabled={loading || !file}
            onClick={async () => {
              if (!file) return;
              try {
                setLoading(true);
                setStatus('uploading');
                const form = new FormData();
                form.append('file', file);
                await new Promise<void>((resolve, reject) => {
                  const xhr = new XMLHttpRequest();
                  xhr.upload.onprogress = (e) => {
                    if (!e.total) return;
                    setUploadProgress((100 / e.total) * e.loaded);
                  };
                  xhr.onreadystatechange = () => {
                    if (xhr.readyState !== 4) return;
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else reject(new Error(`Upload failed: ${xhr.status}`));
                  };
                  xhr.open('POST', '/upload.cgi');
                  xhr.send(form);
                });
                await runAction('install update', 'installing software done');
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setLoading(false);
              }
            }}
          >
            Start Install
          </WebUiButton>
          <WebUiButton
            disabled={loading}
            onClick={() => runAction('reboot', 'reboot requested')}
          >
            Reboot Device
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
